import { type MessageDraftV2 } from '@pubnub/chat';
import { X } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Badge } from '@avanta/shared/components/ui/Badge';
import { Button } from '@avanta/shared/components/ui/Button';
import { ALLOWED_FILE_TYPES } from '@avanta/shared/constants/chat';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useVoiceRecorder } from '@avanta/shared/hooks/chat/useVoiceRecorder';
import { useIsMounted } from '@avanta/shared/hooks/useIsMounted';
import {
  useActiveConversation,
  useConversationsStore,
} from '@avanta/shared/stores/chat/conversations';
import { useMessagesStore } from '@avanta/shared/stores/chat/messages';
import { usePredefinedPhrasesStore } from '@avanta/shared/stores/chat/predefined-phrases';
import { cloneFilesForPubNub } from '@avanta/shared/utils/chat';
import { getUserId } from '@avanta/shared/utils/storage';
import { stripHtmlTags, toRichTextParagraph } from '@avanta/shared/utils/text';
import { AudioPreview, MessageInputRow, RecordingUI } from './components';

export interface InputChatSendPayload {
  sessionId: string;
  senderId: string;
  receiverId: string;
  hasText: boolean;
  files: File[];
  audioFile: File | null;
}

export interface InputChatProps {
  leadingActions?: ReactNode;
  onAfterSend?: (params: InputChatSendPayload) => Promise<void> | void;
}

export const InputChat = ({
  leadingActions,
  onAfterSend,
}: InputChatProps = {}): React.ReactElement => {
  const activeConversation = useActiveConversation();
  const activeChannelId = useConversationsStore(state => state.activeChannelId);
  const { sendTextMessage } = useMessagesStore();
  const methods = useForm({ mode: 'onChange', defaultValues: { message: '' } });
  const {
    handleSubmit,
    formState: { isSubmitting },
    reset,
    setValue,
  } = methods;
  const [draft, setDraft] = useState<MessageDraftV2 | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isSending, setIsSending] = useState(false);
  const { isRecording, startRecording, stopRecording, audioFile, recordingTime, resetRecording } =
    useVoiceRecorder();
  const isMounted = useIsMounted();
  const { t } = useTranslation(LOCIZE_NS.CHAT);
  const setPhraseHandler = usePredefinedPhrasesStore(state => state.setPhraseHandler);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const conversationRef = useRef(activeConversation);
  conversationRef.current = activeConversation;

  useEffect(() => {
    const channel = activeConversation?.channel;
    if (!activeChannelId || !channel) return;

    const newDraft = channel.createMessageDraftV2({
      isTypingIndicatorTriggered: true,
    });
    setDraft(newDraft);
    reset();
    return (): void => {
      setDraft(null);
    };
  }, [activeChannelId, activeConversation?.channel, reset]);

  useEffect(() => {
    setPhraseHandler((phrase: string): void => {
      const currentDraft = draftRef.current;
      const currentConv = conversationRef.current;
      if (!currentDraft) return;

      const htmlContent = toRichTextParagraph(phrase);
      setValue('message', htmlContent, { shouldDirty: true, shouldValidate: true });
      currentDraft.update(phrase);

      if (currentConv?.channel && phrase.trim()) {
        currentConv.channel.startTyping();
      }
    });

    return (): void => setPhraseHandler(null);
  }, [setPhraseHandler, setValue]);

  const handleSendMessage = useCallback(
    async (data: { message: string }): Promise<void> => {
      const htmlContent = data.message;
      const plainText = stripHtmlTags(htmlContent);
      const hasContent = plainText || selectedFiles.length > 0 || audioFile;

      if (!hasContent || !isMounted() || !activeChannelId) return;

      try {
        setIsSending(true);
        const channel = activeConversation?.channel;

        if (channel) {
          await channel.stopTyping();
        }

        const sentFiles = selectedFiles;
        const sentAudio = audioFile;
        const params: InputChatSendPayload = {
          sessionId: activeChannelId,
          senderId: getUserId(),
          receiverId: activeConversation?.otherUser.id || '',
          hasText: Boolean(plainText),
          files: sentFiles,
          audioFile: sentAudio,
        };

        if (selectedFiles.length > 0 || audioFile) {
          if (!draft) {
            toast.error(t('Chat.Messages.send.error'));
            return;
          }
          if (selectedFiles.length > 0) {
            draft.files = cloneFilesForPubNub(selectedFiles);
          }
          if (audioFile) {
            draft.files = cloneFilesForPubNub([audioFile]);
            resetRecording();
          }
          await draft.send();
        }
        if (plainText) {
          await sendTextMessage(activeChannelId, htmlContent);
        }

        if (draft) draft.update('');
        reset();
        setSelectedFiles([]);
        await onAfterSend?.(params);
      } catch {
        toast.error(t('Chat.Messages.send.error'));
      } finally {
        setIsSending(false);
      }
    },
    [
      activeChannelId,
      activeConversation,
      audioFile,
      draft,
      isMounted,
      reset,
      resetRecording,
      selectedFiles,
      sendTextMessage,
      onAfterSend,
      t,
    ]
  );

  const handleMessageChange = useCallback(
    (value: string) => {
      const plainText = stripHtmlTags(value);
      if (activeConversation?.channel && plainText.trim()) {
        activeConversation.channel.startTyping();
      }
      if (draft) {
        draft.update(plainText);
      }
    },
    [activeConversation, draft]
  );

  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
    const files = event.target.files;
    if (!files) return;
    setSelectedFiles(prevFiles => [...prevFiles, ...Array.from(files)]);
  }, []);

  const handleRemoveFile = useCallback(
    (lastModified: number): void => {
      setSelectedFiles(prevFiles => prevFiles.filter(file => file.lastModified !== lastModified));
      if (audioFile && audioFile.lastModified === lastModified) {
        resetRecording();
      }
    },
    [audioFile, resetRecording]
  );

  const handleStartRecording = useCallback((): void => {
    if (isRecording) {
      stopRecording();
      return;
    }
    startRecording();
  }, [isRecording, startRecording, stopRecording]);

  const handleAttachClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return (
    <FormProvider {...methods}>
      <form
        data-chat-form
        onSubmit={handleSubmit(handleSendMessage)}
        className="w-full border-t border-plum-200 px-2.5 py-2 max-am-xl:p-2 bg-plum-100 shrink-0 am-xl:block sticky bottom-0 left-0 right-0 z-10"
      >
        <div className="flex flex-col gap-2 w-full">
          {selectedFiles && selectedFiles.length > 0 && !audioFile && (
            <div className="flex items-center gap-2 flex-wrap">
              {selectedFiles.map(file => (
                <Badge
                  key={`${file.name}-${file.lastModified}`}
                  size="sm"
                  className="cursor-pointer rounded-sm bg-white text-denim-600 border border-plum-200 h-8"
                >
                  {file.name}
                  <Button
                    variant="ghost"
                    type="button"
                    className="p-0 size-6 hover:text-red-500 hover:bg-transparent"
                    onClick={() => handleRemoveFile(file.lastModified)}
                  >
                    <X />
                  </Button>
                </Badge>
              ))}
            </div>
          )}
          <div className="flex gap-2 w-full justify-between">
            <input
              ref={fileInputRef}
              className="hidden"
              accept={Object.values(ALLOWED_FILE_TYPES).join(',')}
              type="file"
              multiple
              onChange={handleFileChange}
            />
            {isRecording ? (
              <RecordingUI
                isRecording={isRecording}
                isSending={isSending}
                recordingTime={recordingTime}
                recordingLabel={t('Chat.Messages.states.recording')}
                onRecordClick={handleStartRecording}
              />
            ) : audioFile ? (
              <AudioPreview
                audioFile={audioFile}
                isSubmitting={isSubmitting}
                onRemove={() => handleRemoveFile(audioFile.lastModified)}
              />
            ) : (
              <MessageInputRow
                selectedFilesCount={selectedFiles.length}
                isRecording={isRecording}
                isSending={isSending}
                isSubmitting={isSubmitting}
                leadingActions={leadingActions}
                onAttachClick={handleAttachClick}
                onMessageChange={handleMessageChange}
                onRecordClick={handleStartRecording}
              />
            )}
          </div>
        </div>
      </form>
    </FormProvider>
  );
};
